import React, { useState } from 'react';
import { Grid } from '@mui/material';
import { SupplierBank } from 'types/supplier';
import { Bank } from 'types/bank';
import SupplierBanksList from './list/SupplierBanksList';
import SupplierBankForm from './form/SupplierBankForm';
import ConfirmDialog from 'components/confirm-dialog/ConfirmDialog';
import { countLabel, RecordToolbar } from 'components/record-shell/RecordList';
import { Supplier } from 'types/supplier';

interface SupplierBanksProps {
  supplier: Supplier;
  banks: Bank[];
  handleChangeAddBank(bank: SupplierBank): void;
  handleDeleteBank(bank: SupplierBank): void;
}

const SupplierBanks: React.FC<SupplierBanksProps> = ({ supplier, handleDeleteBank, handleChangeAddBank, banks }) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<SupplierBank | null>(null);
  const [bankInfo, setBankInfo] = useState<SupplierBank>({
    bank_account: '',
    bank_agency: '',
    bank_name: '',
    supplier_id: supplier.id,
    id: 0,
    pix_key: '',
    pix_key_type: '',
    account_holder: '',
    operation: '',
  });

  function handleChangeBankInfo<K extends keyof SupplierBank>(index: K, value: SupplierBank[K]) {
    setBankInfo(prev => ({ ...prev, [index]: value }));
  }

  function handleAddBank(bankInfo: SupplierBank | null) {
    if (!bankInfo) return;
    handleChangeAddBank(bankInfo);
    setAddDialogOpen(false);
    setBankInfo({
      bank_account: '',
      bank_agency: '',
      bank_name: '',
      supplier_id: supplier.id,
      id: 0,
      pix_key: '',
      pix_key_type: '',
      account_holder: '',
      operation: '',
    });
  }

  function onExited() {
    setAddDialogOpen(false);
    setBankInfo({
      bank_account: '',
      bank_agency: '',
      bank_name: '',
      supplier_id: supplier.id,
      id: 0,
      pix_key: '',
      pix_key_type: '',
      account_holder: '',
      operation: '',
    });
    setEditDialogOpen(false);
  }

  function handleEditBank(bank: SupplierBank, index: number) {
    setEditDialogOpen(true);
    setBankInfo(bank);
  }

  return (
    <Grid container size={12} spacing={2}>
      {confirmDialogOpen && (
        <ConfirmDialog
          confirmLabel="Excluir"
          destructive
          handleNoClick={() => setConfirmDialogOpen(null)}
          handleYesClick={() => {
            handleDeleteBank(confirmDialogOpen);
            setConfirmDialogOpen(null);
          }}
          title="Tem certeza que deseja excluir esta conta bancária?"
        />
      )}
      <Grid size={12}>
        <RecordToolbar
          title={countLabel(supplier.banks.length, 'conta bancária', 'contas bancárias', 'Nenhuma conta bancária')}
          addLabel="Adicionar conta"
          onAdd={() => setAddDialogOpen(true)}
        />
      </Grid>

      {supplier.banks.length > 0 && (
        <Grid size={12}>
          <SupplierBanksList
            banks={supplier.banks}
            handleDeleteBank={item => setConfirmDialogOpen(item)}
            handleEditBank={handleEditBank}
          />
        </Grid>
      )}

      {addDialogOpen && (
        <SupplierBankForm
          type="new"
          handleChangeBankInfo={handleChangeBankInfo}
          bankInfo={bankInfo}
          handleAddBank={handleAddBank}
          onExited={onExited}
          banks={banks}
        />
      )}

      {!!bankInfo.id && editDialogOpen && (
        <SupplierBankForm
          type="edit"
          handleChangeBankInfo={handleChangeBankInfo}
          bankInfo={bankInfo}
          handleAddBank={handleAddBank}
          onExited={onExited}
          banks={banks}
        />
      )}
    </Grid>
  );
};

export default SupplierBanks;
